import { useCallback } from "react";
import { getCTAUrl } from "../../../utils/getCTA";
import { useNavigate } from "react-router";
import { Comic, Release } from "../../../types";
import {
  useGetReaderNavigationContext,
  UseGetReaderNavigationContextProps,
} from "./useGetReaderNavigationContext";
import { readerNavigationContext } from "../../../hookstate/readerNavigationContextStore";
import { ReaderNavigationContext } from "../types";

export function useNavigateToReaderWithContext(
  {
    comicToNavigate,
    releaseToNavigate,
    trigger,
    contextComicId,
    section,
    cardPosition,
    sectionPosition,
    notificationTypeId,
    area,
    pageToNavigate,
  }: UseGetReaderNavigationContextProps & {
    releaseToNavigate?: Release;
    comicToNavigate?: Comic;
    pageToNavigate?: number;
  },
  dependencies: ReadonlyArray<unknown> = [],
) {
  const navigationContext = useGetReaderNavigationContext({
    trigger,
    contextComicId,
    section,
    cardPosition,
    sectionPosition,
    notificationTypeId,
    area,
  });

  const navigate = useNavigate();

  const navigateToCta = useCallback(
    (overrides?: Partial<ReaderNavigationContext>) => {
      readerNavigationContext.set({
        ...navigationContext,
        ...(overrides ?? {}),
      });
      const entityToNavigate = releaseToNavigate ?? comicToNavigate;
      if (entityToNavigate) {
        let ctaUrl = getCTAUrl(entityToNavigate);
        if (pageToNavigate) {
          ctaUrl = ctaUrl.replace(/\/\d+$/, `/${pageToNavigate}`);
        }

        navigate(ctaUrl);
      }
    },
    [
      navigationContext,
      releaseToNavigate,
      comicToNavigate,
      pageToNavigate,
      navigate,
    ],
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  const goToCTAUrlWithContext = useCallback(
    (overrides?: Partial<ReaderNavigationContext>) => {
      navigateToCta(overrides);
    },
    [navigateToCta, ...dependencies],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  const goToCTAUrl = useCallback(() => {
    navigateToCta();
  }, [navigateToCta]);

  return {
    goToCTAUrl,
    goToCTAUrlWithContext,
    navigationContext,
  };
}
